import { Dialog, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { CITIES, switchCity, useCity } from './useCity';

/**
 * "Where are you?", once: the app (not the landing page) opened with no
 * `cg.city` stored. One tap on a live city chooses it and it is remembered.
 * Never guessed from the browser's location.
 *
 * Like the mobile notice, no ✕ and no click-away: with one city live the
 * choice is one tap, and leaving it unmade would only ask again next visit.
 */

const Panel = styled('div')({
  background: BRAND.mochaDeep,
  color: BRAND.parchment,
  padding: '30px 26px 26px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 20,
});

const Choices = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  width: '100%',
});

const Choice = styled('button')({
  appearance: 'none',
  minHeight: 96,
  borderRadius: 18,
  border: '1.5px solid rgba(243,238,231,0.7)',
  background: BRAND.oliveLeaf,
  color: BRAND.parchment,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '14px 10px',
  transition: 'filter 120ms ease',
  '&:hover:not(:disabled)': { filter: 'brightness(1.15)' },
  '&:disabled': {
    cursor: 'default',
    background: 'transparent',
    borderColor: 'rgba(243,238,231,0.25)',
    color: 'rgba(243,238,231,0.5)',
  },
});

export default function CityChooser() {
  const { chosen } = useCity();
  return (
    <Dialog
      open={!chosen}
      // No `onClose`: neither Escape nor a backdrop tap dismisses it.
      maxWidth="xs"
      fullWidth
      aria-labelledby="city-chooser-heading"
      slotProps={{
        paper: { sx: { background: BRAND.mochaDeep, backgroundImage: 'none', borderRadius: 4, m: 2 } },
      }}
    >
      <Panel>
        <Typography
          id="city-chooser-heading"
          component="h2"
          sx={{ fontFamily: FONTS.condensed, fontWeight: 500, fontSize: 40, lineHeight: 1 }}
        >
          Where are you?
        </Typography>
        <Choices>
          {CITIES.map((c) => (
            <Choice key={c.key} type="button" disabled={!c.enabled} onClick={() => switchCity(c.key)}>
              <span style={{ fontFamily: FONTS.condensed, fontWeight: 500, fontSize: 32, lineHeight: 1 }}>
                {c.short}
              </span>
              <span style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 700, opacity: 0.85 }}>
                {c.enabled ? c.label : 'Coming soon'}
              </span>
            </Choice>
          ))}
        </Choices>
      </Panel>
    </Dialog>
  );
}
